import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  Mail,
  Loader2,
  AlertTriangle,
  Trash2,
  Upload,
  CheckCircle2,
} from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import ConfirmModal from '@/components/shared/ConfirmModal';
import UploadField from '@/components/match/sessions/UploadField';
import MagicMapFlowModal from '@/components/match/sessions/MagicMapFlowModal';

const BASE = `${API_URL}/api/match`;

// ---------------------------------------------------------------------------
// Badge components
// ---------------------------------------------------------------------------

const SESSION_STATUS_CLS = {
  pending:    'bg-boss-elevated text-boss-text-muted border-boss-border',
  created:    'bg-boss-elevated text-boss-text-muted border-boss-border',
  processing: 'bg-boss-status-info-bg text-boss-status-info border-[var(--boss-info-border)]',
  processed:  'bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]',
  done:       'bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]',
  complete:   'bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]',
  trigger_failed: 'bg-boss-status-danger-bg text-boss-status-danger border-[var(--boss-danger-border)]',
  error:      'bg-boss-status-danger-bg text-boss-status-danger border-[var(--boss-danger-border)]',
};

const SESSION_STATUS_LABEL = {
  pending:    'Pending',
  created:    'Created',
  processing: 'Processing',
  processed:  'Processed',
  done:       'Done',
  complete:   'Done',
  trigger_failed: 'Trigger failed',
  error:      'Error',
};

function SessionStatusBadge({ status }) {
  const cls = SESSION_STATUS_CLS[status] || 'bg-boss-elevated text-boss-text-muted border-boss-border';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {status === 'processing' && <Loader2 size={10} className="animate-spin" />}
      {SESSION_STATUS_LABEL[status] || status}
    </span>
  );
}

/**
 * SanitizeStatsBadge
 *
 * Renders a muted chip when a session's sanitize_stats show rows were dropped.
 * Only shown when rows_dropped > 0; no chip for clean passes or absent stats.
 */
export function SanitizeStatsBadge({ stats }) {
  if (!stats || stats.rows_dropped == null || stats.rows_dropped <= 0) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-boss-elevated text-boss-text-muted border-boss-border ml-1.5"
      title={`${stats.rows_kept ?? '?'} of ${stats.rows_in ?? '?'} rows kept after applying the template's structure recipe`}
      data-testid="sanitize-stats-chip"
    >
      {stats.rows_dropped} junk rows removed
    </span>
  );
}

function SourceBadge({ source }) {
  if (source === 'email_forward') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-boss-accent-soft text-boss-accent border-boss-border-active">
        <Mail size={10} />
        Email
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-boss-elevated text-boss-text-muted border-boss-border">
      Manual
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Template auto-match
// Returns templates whose name contains the provider string, or vice versa.
// ---------------------------------------------------------------------------

function matchTemplates(provider, templates) {
  if (!provider?.trim() || !templates.length) return [];
  const q = provider.toLowerCase().trim();
  return templates.filter((t) => {
    const n = (t.name || '').toLowerCase();
    return n.includes(q) || q.includes(n);
  });
}

// ---------------------------------------------------------------------------
// FastAPI 422 responses carry `detail` as an ARRAY of error objects; putting
// that straight into error state would crash at render. Always reduce to a string.
// ---------------------------------------------------------------------------

function asErrorText(detail, fallback) {
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === 'string' ? d : d?.msg))
      .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (detail && typeof detail === 'object') {
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Import Panel — primary import-first action
// ---------------------------------------------------------------------------

const EMPTY_UPLOADS = {
  provider_report:    null,
  snapshot_customers: null,
  snapshot_orders:    null,
};

function ImportPanel({ templates, onSuccess, canMagicMap }) {
  const [label,            setLabel]            = useState('');
  const [mode,             setMode]             = useState('live_api');
  const [uploads,          setUploads]          = useState(EMPTY_UPLOADS);
  const [detectedProvider, setDetectedProvider] = useState('');
  const [templateId,       setTemplateId]       = useState('');
  const [autoMatched,      setAutoMatched]       = useState(null);  // template obj
  const [ambiguous,        setAmbiguous]         = useState(false); // multiple matches
  const [advancedOpen,     setAdvancedOpen]      = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [error,            setError]            = useState(null);
  const [magicMapOpen,     setMagicMapOpen]     = useState(false);

  const reportUploaded  = !!uploads.provider_report;
  const showSnapshotUploads = mode === 'upload';

  function setUpload(slot, info) {
    setUploads((prev) => ({ ...prev, [slot]: info }));
  }

  function handleReportUpload(info) {
    setUploads((prev) => ({ ...prev, provider_report: info }));
    setError(null);
    setMagicMapOpen(false);
    if (!info) {
      setDetectedProvider('');
      setTemplateId('');
      setAutoMatched(null);
      setAmbiguous(false);
      return;
    }
    const provider = info.detected_provider || '';
    setDetectedProvider(provider);
    if (provider) {
      const matches = matchTemplates(provider, templates);
      if (matches.length === 1) {
        setTemplateId(matches[0].template_id);
        setAutoMatched(matches[0]);
        setAmbiguous(false);
      } else {
        // 0 or multiple matches — let the user pick
        setTemplateId('');
        setAutoMatched(null);
        setAmbiguous(matches.length > 1);
      }
    } else {
      setTemplateId('');
      setAutoMatched(null);
      setAmbiguous(false);
    }
  }

  // Returns true when a report is uploaded but no template can be resolved,
  // AND the user has not manually selected one.  This is the dead-end that
  // Magic Mapping intercepts. Ambiguous (MULTIPLE templates match) is NOT
  // missing — that state has its own picker; offering Magic Map there would
  // push users into creating duplicate templates.
  function isTemplateMissing() {
    return reportUploaded && !templateId && !autoMatched && !ambiguous;
  }

  // Excel-converted reports are usually raw grouped exports (preamble rows,
  // headers below row 0, subtotal junk). A template WITHOUT a parse recipe
  // reads that grid with the preamble as its header and every mapped column
  // comes up empty — the import then dies with "Live fetch has nothing to
  // search on" (hit live 2026-07-10 with a Magic-Mapped template whose
  // recipe had been dropped). Warn BEFORE the dead-end and offer the Magic
  // Map flow, which captures the recipe.
  const recipeMismatch =
    reportUploaded &&
    Boolean(uploads.provider_report?.converted_from_xlsx) &&
    Boolean(templateId) &&
    !templates.find((t) => t.template_id === templateId)?.parse_recipe;

  function validate() {
    if (!uploads.provider_report) return 'Upload the provider report file first.';
    if (!templateId) {
      if (templates.length === 0) return 'No templates available — create one in the Provider Templates tab first.';
      return 'Select a template to continue.';
    }
    if (mode === 'upload' && !uploads.snapshot_customers) {
      return 'Upload the Snapshot Customers file (required for Upload mode).';
    }
    return null;
  }

  // Core session-create + process logic, extracted so the MagicMapFlowModal
  // callback can drive it with an override template after template creation,
  // without duplicating any of the HTTP or error-handling code.
  //
  // override — optional { templateId: string, provider: string }
  //   When provided, these values replace the panel's current templateId /
  //   detectedProvider for this single call (used after Magic Map creates
  //   a new template and the user clicks "Import & Match").
  async function runImport(override) {
    setSaving(true);
    setError(null);
    try {
      const resolvedTemplateId = override?.templateId ?? templateId;
      const selectedTemplate   = templates.find((t) => t.template_id === resolvedTemplateId);
      // || not ?? — detectedProvider defaults to '' (empty string), which ??
      // passes through, silently persisting sessions with provider: "". The
      // template-name fallback exists precisely for undetected-provider files.
      const provider = override?.provider || detectedProvider || selectedTemplate?.name || 'Unknown';
      const body = {
        label:                       label.trim() || null,
        provider,
        mode,
        template_id:                 resolvedTemplateId,
        provider_report_url:         uploads.provider_report?.url         ?? null,
        provider_report_filename:    uploads.provider_report?.filename    ?? null,
        snapshot_customers_url:      uploads.snapshot_customers?.url      ?? null,
        snapshot_customers_filename: uploads.snapshot_customers?.filename ?? null,
        snapshot_orders_url:         uploads.snapshot_orders?.url         ?? null,
        snapshot_orders_filename:    uploads.snapshot_orders?.filename    ?? null,
      };
      const res  = await api.post(`${BASE}/sessions`, body);
      const data = await res.json();
      if (!res.ok) {
        setError(asErrorText(data?.detail, 'Failed to create session.'));
        return;
      }
      const sessionId = data.session_id;
      // Fire process — if it fails, stay on the import panel with a LOUD error
      // so the operator knows something went wrong (the session exists either way).
      try {
        const procRes = await api.post(`${BASE}/sessions/${sessionId}/process`, {});
        if (!procRes.ok) {
          const procErr = await procRes.json().catch(() => ({}));
          setError(
            `Session created (ID: ${sessionId}), but the matching trigger failed: ${asErrorText(procErr?.detail, `HTTP ${procRes.status}`)}`
          );
          return;
        }
      } catch (procExc) {
        setError(`Session created (ID: ${sessionId}), but n8n could not be reached: ${procExc?.message || procExc}`);
        return;
      }
      onSuccess(sessionId);
    } finally {
      setSaving(false);
    }
  }

  async function handleImport(e) {
    e.preventDefault();

    // If the report is uploaded but no template resolved, offer Magic Mapping
    // instead of showing the generic "Select a template" error — but only to
    // users who have the create permission needed to build one.
    if (isTemplateMissing() && canMagicMap) {
      setMagicMapOpen(true);
      return;
    }

    const msg = validate();
    if (msg) { setError(msg); return; }

    await runImport();
  }

  function handleMagicMapCreateAndImport(newTemplateId, newProvider) {
    setMagicMapOpen(false);
    runImport({ templateId: newTemplateId, provider: newProvider });
  }

  return (
    <div className="glass-card p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-boss-text flex items-center gap-2">
          <Upload size={16} className="text-boss-accent" />
          Import Report
        </h2>
        <p className="mt-0.5 text-sm text-boss-text-secondary">
          Drop your provider report to auto-detect provider and start matching.
        </p>
      </div>

      <form onSubmit={handleImport} className="space-y-4">
        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Provider Report upload */}
        <div>
          <label className="block text-xs font-medium text-boss-text-secondary mb-1">
            Provider Report <span className="text-boss-status-danger">*</span>
          </label>
          <UploadField
            label="Provider Report"
            endpoint="/api/match/uploads/provider-report"
            required
            onChange={handleReportUpload}
          />
          {uploads.provider_report?.converted_from_xlsx && (
            <p
              className="mt-1 text-xs text-boss-text-muted"
              data-testid="report-structure-xlsx-note"
            >
              Converted from Excel
              {uploads.provider_report.sheet_name
                ? ` (sheet: ${uploads.provider_report.sheet_name})`
                : ''}
            </p>
          )}
          {detectedProvider && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-boss-text-secondary flex-wrap">
              <CheckCircle2 size={12} className="text-boss-status-success flex-shrink-0" />
              <span>
                Detected provider:{' '}
                <span className="font-medium text-boss-text">{detectedProvider}</span>
              </span>
              {autoMatched && !ambiguous && (
                <>
                  <span className="text-boss-text-muted">·</span>
                  <span className="text-boss-status-success">
                    Template auto-selected:{' '}
                    <span className="font-medium">{autoMatched.name}</span>
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Template selector — shown after a report is uploaded */}
        {reportUploaded && (
          <div>
            <label className="block text-xs font-medium text-boss-text-secondary mb-1">
              Template <span className="text-boss-status-danger">*</span>
            </label>
            {ambiguous && (
              <p className="text-xs text-boss-status-warn mb-1.5">
                Multiple templates match "{detectedProvider}" — pick one.
              </p>
            )}
            {!autoMatched && !ambiguous && detectedProvider && templates.length > 0 && (
              <p className="text-xs text-boss-text-muted mb-1.5">
                No template auto-matched "{detectedProvider}" — select manually.
              </p>
            )}
            <select
              className="input-field w-full"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              required
            >
              <option value="">— select template —</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="mt-1 text-xs text-boss-status-warn">
                No templates yet — create one in the Provider Templates tab first.
              </p>
            )}
          </div>
        )}

        {/* Label */}
        <div>
          <label className="block text-xs font-medium text-boss-text-secondary mb-1">
            Label <span className="text-boss-text-muted font-normal">(optional)</span>
          </label>
          <input
            className="input-field w-full"
            type="text"
            placeholder="e.g. Nextiva May Run"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        {/* Advanced collapsible — preserves upload-mode / manual snapshot capability */}
        <div>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs text-boss-text-secondary hover:text-boss-text transition-colors"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? <ChevronDown size={13} /> : <ChevronRightIcon size={13} />}
            Advanced options
            {mode === 'upload' && (
              <span className="ml-1 text-boss-status-warn">(Upload mode — manual snapshots)</span>
            )}
          </button>

          {advancedOpen && (
            <div className="mt-3 space-y-3 pl-4 border-l border-boss-border">
              {/* Snapshot source mode */}
              <div>
                <label className="block text-xs font-medium text-boss-text-secondary mb-1">
                  Snapshot source
                </label>
                <select
                  className="input-field w-full"
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <option value="live_api">Live API — fetch from Provider Sales automatically</option>
                  <option value="upload">Upload — provide snapshot CSVs manually</option>
                </select>
              </div>

              {/* Snapshot uploads — upload mode only */}
              {showSnapshotUploads && (
                <>
                  <div>
                    <label className="block text-xs text-boss-text-muted mb-1">
                      Snapshot Customers <span className="text-boss-status-danger">*</span>
                    </label>
                    <UploadField
                      label="Snapshot Customers"
                      endpoint="/api/match/uploads/snapshot?kind=customers"
                      required
                      onChange={(info) => setUpload('snapshot_customers', info)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-boss-text-muted mb-1">
                      Snapshot Orders{' '}
                      <span className="text-boss-text-muted font-normal">(recommended)</span>
                    </label>
                    <UploadField
                      label="Snapshot Orders"
                      endpoint="/api/match/uploads/snapshot?kind=orders"
                      onChange={(info) => setUpload('snapshot_orders', info)}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Excel report + recipe-less template = near-certain parse failure */}
        {recipeMismatch && (
          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-boss-status-warn-bg border border-[var(--boss-warning-border)] text-boss-status-warn text-sm"
            data-testid="recipe-mismatch-warning"
          >
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              This report was converted from Excel, but the selected template has no
              structure recipe. If the report has preamble rows, headers below the first
              row, or subtotal lines, the import will fail to read it.{' '}
              {canMagicMap && (
                <button
                  type="button"
                  className="underline font-medium hover:text-boss-text transition"
                  onClick={() => setMagicMapOpen(true)}
                  data-testid="recipe-mismatch-magic-map-btn"
                >
                  Magic Map this report instead
                </button>
              )}
            </span>
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={saving || !reportUploaded}
            className="px-5 py-2 rounded-lg text-sm font-medium btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            data-testid="magic-map-flow-import-panel-submit"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            {saving ? 'Importing…' : 'Import & Match'}
          </button>
          {!reportUploaded && (
            <span className="text-xs text-boss-text-muted">Upload a report file to continue</span>
          )}
        </div>
      </form>

      <MagicMapFlowModal
        key={uploads.provider_report?.url || 'no-report'}
        isOpen={magicMapOpen}
        reportUploadInfo={uploads.provider_report}
        offerReason={recipeMismatch
          ? 'The selected template has no structure recipe for this Excel-converted report — its column mappings would be read against the wrong header row.'
          : undefined}
        detectedProvider={detectedProvider}
        asErrorText={asErrorText}
        onCreateAndImport={handleMagicMapCreateAndImport}
        onClose={() => setMagicMapOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionsTab
// ---------------------------------------------------------------------------

export default function SessionsTab({ onNavigate }) {
  const { hasPermission } = usePermissions();
  const canView      = hasPermission('match.session.view');
  const canCreate    = hasPermission('match.session.create');
  const canDelete    = hasPermission('match.session.delete');
  const canMagicMap  = hasPermission('match.provider_template.create');

  const [sessions,     setSessions]     = useState([]);
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const [sessRes, tmplRes] = await Promise.all([
        api.get(`${BASE}/sessions`),
        api.get(`${BASE}/provider-templates`),
      ]);
      const [sessData, tmplData] = await Promise.all([sessRes.json(), tmplRes.json()]);
      if (!sessRes.ok) throw new Error(sessData?.detail || 'Failed to load sessions.');
      setSessions(unwrap(sessData));
      if (tmplRes.ok) setTemplates(unwrap(tmplData));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleImportSuccess(newSessionId) {
    loadData();
    if (onNavigate) onNavigate('board', { session: newSessionId });
  }

  async function handleDelete(session) {
    try {
      const res = await api.delete(`${BASE}/sessions/${session.session_id}`);
      if (!res.ok) {
        const data = await res.json();
        setError(data?.detail || 'Failed to delete session.');
        return;
      }
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-48 text-boss-text-secondary text-sm">
        You don't have permission to view sessions.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Import panel — primary action */}
      {canCreate && (
        <ImportPanel
          templates={templates}
          onSuccess={handleImportSuccess}
          canMagicMap={canMagicMap}
        />
      )}

      {error && (
        <div className="px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm">
          {error}
        </div>
      )}

      {/* Import History */}
      <div>
        <h2 className="text-base font-semibold text-boss-text mb-3">Import History</h2>
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-36 text-boss-text-secondary text-sm">
              Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex items-center justify-center h-36 text-boss-text-secondary text-sm">
              No imports yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-boss-text">
                <thead>
                  <tr className="border-b border-boss-border text-xs text-boss-text-secondary uppercase tracking-wide">
                    <th className="px-4 py-3 text-left font-medium">Label</th>
                    <th className="px-4 py-3 text-left font-medium">Provider</th>
                    <th className="px-4 py-3 text-left font-medium">Source</th>
                    <th className="px-4 py-3 text-left font-medium">Report File</th>
                    <th className="px-4 py-3 text-left font-medium">Created</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Rows</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-boss-border">
                  {sessions.map((s) => (
                    <tr
                      key={s.session_id}
                      className="hover:bg-boss-elevated transition-colors"
                    >
                      <td className="px-4 py-3 font-medium max-w-[160px] truncate">
                        {s.label || (
                          <span className="text-boss-text-muted font-mono text-xs">
                            {s.session_id}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-boss-text-secondary">
                        {s.provider || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <SourceBadge source={s.source} />
                      </td>
                      <td className="px-4 py-3 text-xs text-boss-text-muted max-w-[140px] truncate">
                        {s.provider_report_filename || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-boss-text-secondary whitespace-nowrap">
                        {fmtDate(s.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {s.status ? (
                          <SessionStatusBadge status={s.status} />
                        ) : (
                          <span className="text-boss-text-muted text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-boss-text-muted">
                        <span>{s.row_count != null ? s.row_count : '—'}</span>
                        <SanitizeStatsBadge stats={s.sanitize_stats} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            title="View on Board"
                            onClick={() => onNavigate && onNavigate('board', { session: s.session_id })}
                            className="p-1 rounded-md text-boss-text-muted hover:text-boss-accent hover:bg-boss-accent-soft transition"
                          >
                            <ExternalLink size={15} />
                          </button>
                          {canDelete && (
                            <button
                              title="Delete session"
                              onClick={() => setDeleteTarget(s)}
                              className="p-1 rounded-md text-boss-text-muted hover:text-boss-status-danger hover:bg-boss-status-danger-bg transition"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete session?"
        message={`This will also delete all rows belonging to session "${deleteTarget?.label || deleteTarget?.session_id}". This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
